import { DownloadOutlined, FileExcelOutlined, FileOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Dropdown,
  message,
  Modal,
  Space,
  Table,
  Typography,
  Spin,
} from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Outlet } from 'react-router-dom';

import useAPIClient from '../api/useAPIClient';
import useAPIMutation from '../api/useAPIMutation';
import useAPIQuery from '../api/useAPIQuery';
import ErrorResult from '../components/ErrorResult';
import Page from '../components/Page';
import PageContent from '../components/PageContent';
import PageTitle from '../components/PageTitle';
import { withRouteChange } from '../services/APM';
import { ReportCollection, ReportCollectionData } from '../types';
import { downloadFileFromEndpoint } from '../utils';
import styles from './Reports.module.css';

const REPORT_URLS = {
  APPLICATIONS: '/recruitment-processes/',
  REQUEST_JOB_POSTING: '/request-job-postings/',
};
interface ReportColumn {
  type: ReportCollection['type'];
  title: string;
  subtitle: string;
  report?: ReportCollection;
}

const Reports = () => {
  const { t } = useTranslation();
  const api = useAPIClient();

  const reports = useAPIQuery('/reports/');
  const queryClient = useQueryClient();

  const [loadingDownload, setLoadingDownload] = useState(false);
  const [downloadMsg, setDownloadMsg] = useState('');

  const mutation = useAPIMutation(
    ({ type }: ReportCollectionData, api) => api.post('/reports/', { type }),
    {
      onSuccess: () => {
        message.success(t('O relatório está sendo gerado.'));
      },
      onSettled: () => {
        queryClient.invalidateQueries('/reports/');
      },
    }
  );

  const columns: ReportColumn[] = [
    {
      title: t('Candidaturas nas vagas'),
      subtitle: t('Informações das candidaturas recebidas e vagas relacionadas'),
      type: 'APPLICATIONS',
      report: reports.data?.results.find((report) => report.type === 'APPLICATIONS'),
    },
    {
      title: t('Requisição de vagas'),
      subtitle: t('Informações sobre a lista de requisições de vagas'),
      type: 'REQUEST_JOB_POSTING',
      report: reports.data?.results.find((report) => report.type === 'REQUEST_JOB_POSTING'),
    },
  ];

  const { data: { enableReportViaStreaming } = { enableReportViaStreaming: false } } = useAPIQuery(
    '/configs/tenant-configurations/'
  );

  const showConfirmReportGenerationModal = (type: ReportCollection['type']) => {
    Modal.confirm({
      title: t('Gerar novo relatório'),
      content: (
        <>
          <Typography.Text>
            <Trans>Ao gerar um novo relatório a última extração será excluída.</Trans>
          </Typography.Text>
          <br />
          <Typography.Text>
            <Trans>Deseja prosseguir?</Trans>
          </Typography.Text>
        </>
      ),
      okText: t('Sim'),
      onOk: () => mutation.mutate({ type }),
      okButtonProps: {
        loading: mutation.isLoading,
      },
      cancelText: t('Não'),
    });
  };

  const onDownloadError = () => {
    message.error(t('Ocorreu um erro ao gerar o arquivo para download.'));
  };

  useEffect(() => {
    if (loadingDownload) {
      setDownloadMsg(
        t(
          'O download do seu arquivo está em andamento. Isso pode demorar alguns minutos. Por favor, não saia desta tela enquanto esta mensagem estiver sendo exibida.'
        )
      );
    } else {
      setDownloadMsg('');
    }
  }, [loadingDownload, t]);

  return (
    <Page>
      <PageTitle title={t('Relatórios')} />
      <ErrorResult error={reports.error}>
        <PageContent>
          <Outlet />
          {loadingDownload && (
            <div className={styles['downloadOverlay']}>
              <Spin size="large" />
              {downloadMsg && <div className={styles['downloadMsg']}>{downloadMsg}</div>}
            </div>
          )}
          <Card>
            <Space direction="vertical" size="middle">
              <Table<ReportColumn>
                dataSource={columns}
                loading={reports.isLoading || mutation.isLoading}
                rowKey="type"
                pagination={false}
              >
                <Table.Column<ReportColumn>
                  title={t('Tipo')}
                  render={(value, record) => (
                    <>
                      <Typography.Paragraph strong>{record.title}</Typography.Paragraph>
                      <Typography.Text>{record.subtitle}</Typography.Text>
                    </>
                  )}
                />
                <Table.Column<ReportColumn>
                  title={t('Última extração')}
                  render={(value, record) => (
                    <>
                      {record.report && (
                        <Typography.Text>
                          {record.report.status === 'IN_PROGRESS' && (
                            <Button icon={<FileOutlined />} disabled>
                              {' '}
                              <Trans>Carregando relatório</Trans>
                            </Button>
                          )}
                          {record.report.status === 'SUCCESS' &&
                            (enableReportViaStreaming ? (
                              <Dropdown
                                menu={{
                                  items: [
                                    {
                                      key: 'csv',
                                      label: (
                                        <a
                                          onClick={() =>
                                            downloadFileFromEndpoint(
                                              api,
                                              REPORT_URLS[record.type] + `export-report-data-csv/`,
                                              setLoadingDownload,
                                              onDownloadError
                                            )
                                          }
                                        >
                                          <FileOutlined /> CSV (.csv)
                                        </a>
                                      ),
                                    },
                                    {
                                      key: 'xlsx',
                                      label: (
                                        <a
                                          onClick={() =>
                                            downloadFileFromEndpoint(
                                              api,
                                              REPORT_URLS[record.type] + `export-report-data-xlsx/`,
                                              setLoadingDownload,
                                              onDownloadError
                                            )
                                          }
                                        >
                                          <FileExcelOutlined /> Microsoft Excel (.xlsx)
                                        </a>
                                      ),
                                    },
                                  ],
                                }}
                              >
                                <Button icon={<DownloadOutlined />}>
                                  {' '}
                                  <Trans>Relatório de</Trans>&nbsp;
                                  {record.report?.updated_at
                                    ? moment(record.report.updated_at).format('LLL')
                                    : ''}{' '}
                                </Button>
                              </Dropdown>
                            ) : (
                              <Dropdown
                                menu={{
                                  items: [
                                    {
                                      key: 'csv',
                                      label: (
                                        <a href={record.report?.csv ?? ''}>
                                          <FileOutlined /> CSV (.csv)
                                        </a>
                                      ),
                                    },
                                    {
                                      key: 'xlsx',
                                      label: (
                                        <a href={record.report.xlsx ?? ''}>
                                          <FileExcelOutlined /> Microsoft Excel (.xlsx)
                                        </a>
                                      ),
                                    },
                                  ],
                                }}
                              >
                                <Button icon={<DownloadOutlined />}>
                                  {' '}
                                  <Trans>Relatório de</Trans>&nbsp;
                                  {moment(record.report?.updated_at).format('LLL')}{' '}
                                </Button>
                              </Dropdown>
                            ))}
                          {record.report.status === 'FAILED' && (
                            <Alert
                              type="error"
                              description={
                                <Typography.Text>
                                  <Trans>Erro ao gerar o relatório.</Trans>
                                  <br />
                                  <Trans>Contate o suporte.</Trans>
                                </Typography.Text>
                              }
                              showIcon
                            />
                          )}
                        </Typography.Text>
                      )}
                    </>
                  )}
                />
                <Table.Column<ReportColumn>
                  title={t('Ações')}
                  render={(value, record) => (
                    <Button
                      icon={<PlusOutlined />}
                      disabled={record.report?.status === 'IN_PROGRESS'}
                      onClick={() =>
                        record.report
                          ? showConfirmReportGenerationModal(record.type)
                          : mutation.mutate({ type: record.type })
                      }
                    >
                      {' '}
                      <Trans>Gerar novo relatório</Trans>
                    </Button>
                  )}
                />
              </Table>
            </Space>
          </Card>
        </PageContent>
      </ErrorResult>
    </Page>
  );
};

export default withRouteChange()(Reports);
